import { api } from "@/api/client";
import { unwrap } from "@/api/errors";
import type { FileOut } from "@/api/types";
import i18n from "@/i18n";

export type ProjectFileOut = FileOut;

export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const UPLOAD_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  zip: "application/zip",
};
export const UPLOAD_ACCEPT = Object.keys(UPLOAD_TYPES)
  .map((e) => `.${e}`)
  .join(",");

export class UploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UploadError";
  }
}

export function extensionOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i < 0 ? "" : name.slice(i + 1).toLowerCase();
}

/** Client-side checks mirroring the API. Returns the user-facing message, or null when fine. */
export function checkUpload(file: File): string | null {
  if (!(extensionOf(file.name) in UPLOAD_TYPES)) return i18n.t("files.typeNotAllowed");
  if (file.size > UPLOAD_MAX_BYTES) return i18n.t("files.tooLarge");
  return null;
}

function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new UploadError(i18n.t("files.uploadFailed")));
      }
    });
    xhr.addEventListener("error", () => reject(new UploadError(i18n.t("files.uploadFailed"))));
    xhr.send(file);
  });
}

/** Validate, presign, PUT to S3 with progress, then register the upload with the API. */
export async function uploadFile(
  orgSlug: string,
  projectSlug: string,
  file: File,
  meta: { title: string; description?: string },
  onProgress?: (pct: number) => void,
): Promise<ProjectFileOut> {
  const problem = checkUpload(file);
  if (problem) throw new UploadError(problem);
  const path = { slug: orgSlug, pslug: projectSlug };
  const contentType = UPLOAD_TYPES[extensionOf(file.name)] ?? file.type;
  const presign = unwrap(
    await api.POST("/orgs/{slug}/projects/{pslug}/files/presign", {
      params: { path },
      body: { filename: file.name, content_type: contentType, size_bytes: file.size },
    }),
  );
  await putWithProgress(presign.upload_url, file, { "Content-Type": contentType, ...presign.headers }, onProgress);
  return unwrap(
    await api.POST("/orgs/{slug}/projects/{pslug}/files", {
      params: { path },
      body: { key: presign.key, title: meta.title, ...(meta.description ? { description: meta.description } : {}) },
    }),
  );
}
